import {
  experimentalQueryPersister,
  SelectedFilters,
} from '@lidea/shared/data-access/core';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { plotsApi } from '../apis/plots-api';

const getPageQueryParam = (url: string) => {
  const params = new URL(url).searchParams;
  const page = params.get('page');

  if (page) {
    return parseInt(page);
  }
  return null;
};

export const plotKeys = {
  all: (campaignId?: number | null) =>
    campaignId ? (['plots', { campaignId }] as const) : (['plots'] as const),
  maxBounds: (campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'maxBounds',
  ],
  searches: (campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'search',
  ],
  search: (filters: SelectedFilters, campaignId?: number | null) =>
    infiniteQueryOptions({
      queryKey: [...plotKeys.searches(campaignId), filters],
      initialPageParam: 1,
      queryFn: ({ pageParam }) => plotsApi.getAll(filters, pageParam),
      getNextPageParam: (lastPage) =>
        lastPage.next ? getPageQueryParam(lastPage.next) : undefined,
      getPreviousPageParam: (prevPage) =>
        prevPage.previous ? getPageQueryParam(prevPage.previous) : undefined,
    }),
  plotTypeahead: (
    q: string,
    filters: SelectedFilters,
    campaignId?: number | null
  ) =>
    queryOptions({
      queryKey: [...plotKeys.all(campaignId), 'plotTypeahead', { q, filters }],
      queryFn: () => plotsApi.searchPlots(q, filters),
    }),
  paginated: ({
    page,
    filters,
    sort,
    campaignId,
  }: {
    page: number;
    filters: SelectedFilters;
    sort?: string;
    campaignId?: number | null;
  }) =>
    queryOptions({
      queryKey: [
        ...plotKeys.search(filters, campaignId).queryKey,
        'paginated',
        {
          page,
          sort,
        },
      ],
      queryFn: () => plotsApi.getAll(filters, page, sort),
    }),
  inBound: (campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'inBounds',
  ],
  inBounds: (
    bounds: mapboxgl.LngLatBoundsLike,
    cluster: boolean,
    filters: SelectedFilters,
    zoom: number,
    type: string | undefined,
    campaignId?: number | null
  ) => [
    ...plotKeys.inBound(campaignId),
    { bounds, cluster, filters, zoom, type },
  ],
  details: (campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'detail',
  ],

  detail: (id: number, campaignId?: number | null) =>
    queryOptions({
      queryKey: [...plotKeys.details(campaignId), id],
      queryFn: () => plotsApi.getOne(id),
      persister: experimentalQueryPersister('plots'),
    }),
  yieldMap: (id: number, campaignId?: number | null) => [
    ...plotKeys.detail(id, campaignId).queryKey,
    'yieldMap',
  ],
  analysisStatuses: (id: number, campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'analysisStatuses',
    id,
  ],
  canMerge: (ids: number[], campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'canMerge',
    ids,
  ],
  synthesis: (ids: number[], campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'synthesis',
    ids,
  ],
  measuresExportStatus: (taskId: string, campaignId?: number | null) => [
    ...plotKeys.all(campaignId),
    'measuresExportStatus',
    taskId,
  ],
};
